import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getPaymentStatus } from '../services/api';
import { trackPurchase } from '../utils/barionPixel';
import { trackPurchase as trackMetaPurchase } from '../utils/metaPixel';

const PaymentReturnPage = () => {
  const [params] = useSearchParams();
  const orderId = params.get('order_id');
  const [status, setStatus] = useState(() => (
    orderId
      ? { loading: true, data: null, error: null }
      : { loading: false, data: null, error: 'Hiányzó megrendelés azonosító.' }
  ));
  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      try {
        const data = await getPaymentStatus(orderId);
        if (cancelled) return;
        const isFinal = data.status === 'succeeded' || data.status === 'canceled'
                      || data.status === 'failed' || data.status === 'expired';
        if (isFinal || attempts >= 8) {
          setStatus({ loading: false, data, error: null });
        } else {
          attempts += 1;
          setTimeout(tick, 1500);
        }
      } catch (e) {
        if (!cancelled) {
          setStatus({ loading: false, data: null, error: e?.response?.data?.detail || 'Lekérdezés sikertelen.' });
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId]);

  const isSuccess = status.data?.status === 'succeeded';

  // Fire Barion `purchase` pixel event exactly once when payment is confirmed.
  useEffect(() => {
    if (!isSuccess || purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    const d = status.data || {};
    trackPurchase({
      orderId,
      programId:    d.program_id || d.package_name || orderId,
      programTitle: d.package_name || 'Orosz Coaching csomag',
      value:        Number(d.amount_huf || 0),
      currency:     'HUF',
    });
    // Meta Pixel — same event, GDPR-gated inside metaPixel.js.
    trackMetaPurchase({
      value:        Number(d.amount_huf || 0),
      currency:     'HUF',
      content_ids:  [d.program_id || d.package_name || orderId],
      content_name: d.package_name || 'Orosz Coaching csomag',
    });
  }, [isSuccess, orderId, status.data]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-yellow-50/40 flex items-center justify-center p-6">
      <div data-testid="payment-return-page" className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className={`p-8 text-center ${isSuccess ? 'bg-gradient-to-br from-green-50 to-yellow-50' : 'bg-white'}`}>
          {status.loading ? (
            <>
              <Loader2 className="mx-auto text-yellow-600 animate-spin mb-4" size={48} />
              <h1 className="text-2xl font-bold text-gray-900 mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Fizetés feldolgozása…
              </h1>
              <p className="text-gray-600">Egy pillanat, ellenőrizzük a Barion visszajelzését.</p>
            </>
          ) : isSuccess ? (
            <>
              <CheckCircle2 className="mx-auto text-green-500 mb-4" size={56} />
              <div className="text-xs font-bold tracking-widest uppercase text-green-600 mb-2">Sikeres fizetés</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Köszönjük a vásárlásod! 🎉
              </h1>
              <p className="text-gray-700 leading-relaxed">
                A(z) <strong>{status.data?.package_name}</strong> csomag fizetése sikeresen megtörtént.
                Az elektronikus számlád <strong>{status.data?.billing?.email}</strong> címre érkezik,
                és egy üdvözlő e-mailt is küldtünk az első lépésekről.
              </p>
            </>
          ) : (
            <>
              <XCircle className="mx-auto text-red-500 mb-4" size={56} />
              <h1 className="text-2xl font-bold text-gray-900 mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                A fizetés nem fejeződött be
              </h1>
              <p className="text-gray-700">
                Állapot: <strong>{status.data?.status || status.error || 'ismeretlen'}</strong>.
                Ha úgy érzed, ez tévedés, írj a info@oroszcoaching.hu címre a rendelés azonosítóval:{' '}
                <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">{orderId}</code>.
              </p>
            </>
          )}
        </div>

        {isSuccess && (
          <div className="p-6 md:p-8 border-t border-gray-100 space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="text-yellow-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="font-semibold text-gray-900">E-számla úton</div>
                <div className="text-sm text-gray-600">
                  Számlaszám: <strong>{status.data?.invoice_number || 'Generálás folyamatban…'}</strong>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="text-yellow-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="font-semibold text-gray-900">Első lépés</div>
                <div className="text-sm text-gray-600">
                  Az e-mailedben elküldött <strong>kérdőív kitöltése</strong> — ez alapján építem fel a programodat.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 bg-gray-50 text-center">
          <Link to="/">
            <Button className="bg-gray-900 hover:bg-yellow-600 text-white font-bold">
              Vissza a főoldalra
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturnPage;
